import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  ArrowLeft, 
  UserPlus, 
  Loader2, 
  Check, 
  X, 
  Mail, 
  Building2, 
  Clock,
  CheckCircle,
  XCircle,
  Plus,
  Trash2,
  RefreshCw
} from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminAccessRequests() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [whitelist, setWhitelist] = useState([]);
  const [activeTab, setActiveTab] = useState("pending");
  const [processing, setProcessing] = useState(null);
  
  // Add to whitelist dialog
  const [showAddWhitelist, setShowAddWhitelist] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [newCompany, setNewCompany] = useState("");
  const [addingEmail, setAddingEmail] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user, activeTab]);

  const checkAuth = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: "include" });
      if (!res.ok) {
        navigate("/auth");
        return;
      }
      setUser(await res.json());
    } catch {
      navigate("/auth");
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === "whitelist") {
        const res = await fetch(`${BACKEND_URL}/api/admin/whitelist`, { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          setWhitelist(data.whitelist || []);
        }
      } else {
        const status = activeTab === "all" ? "" : activeTab;
        const res = await fetch(`${BACKEND_URL}/api/admin/access-requests?status=${status}`, { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          setRequests(data.requests || []);
        }
      }
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId) => {
    setProcessing(requestId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/access-requests/${requestId}/approve`, {
        method: "POST",
        credentials: "include"
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Failed to approve");
      }
      
      toast.success("Access approved! Invitation email sent.");
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (requestId) => {
    setProcessing(requestId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/access-requests/${requestId}/reject`, {
        method: "POST",
        credentials: "include"
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Failed to reject");
      }
      
      toast.success("Request rejected");
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessing(null);
    }
  };

  const handleRestore = async (requestId) => {
    setProcessing(requestId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/access-requests/${requestId}/restore`, {
        method: "POST",
        credentials: "include"
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Failed to restore access");
      }
      
      toast.success("Access restored! User can sign in again.");
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessing(null);
    }
  };

  const handleAddToWhitelist = async (e) => {
    e.preventDefault();
    if (!newEmail) {
      toast.error("Email is required");
      return;
    }
    
    setAddingEmail(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/whitelist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email: newEmail,
          name: newName,
          company: newCompany
        })
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Failed to add email");
      }
      
      toast.success("Email added to whitelist");
      setShowAddWhitelist(false);
      setNewEmail("");
      setNewName("");
      setNewCompany("");
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setAddingEmail(false);
    }
  };

  const handleRemoveFromWhitelist = async (email) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/whitelist/${encodeURIComponent(email)}`, {
        method: "DELETE",
        credentials: "include"
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Failed to remove");
      }
      
      toast.success("Email removed from whitelist");
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return <Badge className="bg-amber-100 text-amber-700">Pending</Badge>;
      case "approved":
        return <Badge className="bg-green-100 text-green-700">Approved</Badge>;
      case "rejected":
        return <Badge className="bg-red-100 text-red-700">Rejected</Badge>;
      case "revoked":
        return <Badge className="bg-orange-100 text-orange-700">Revoked</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar user={user} />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate("/admin")}>
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <UserPlus className="w-6 h-6 text-amber-500" />
              Access Requests
            </h1>
            <p className="text-slate-500 text-sm">Manage early access program</p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="pending" className="gap-2">
              <Clock className="w-4 h-4" />
              Pending
            </TabsTrigger>
            <TabsTrigger value="approved" className="gap-2">
              <CheckCircle className="w-4 h-4" />
              Approved
            </TabsTrigger>
            <TabsTrigger value="rejected" className="gap-2">
              <XCircle className="w-4 h-4" />
              Rejected
            </TabsTrigger>
            <TabsTrigger value="revoked" className="gap-2">
              <XCircle className="w-4 h-4" />
              Revoked
            </TabsTrigger>
            <TabsTrigger value="whitelist" className="gap-2">
              <Mail className="w-4 h-4" />
              Whitelist
            </TabsTrigger>
          </TabsList>

          {/* Pending, Approved, Rejected, Revoked tabs */}
          {["pending", "approved", "rejected", "revoked"].map((tab) => (
            <TabsContent key={tab} value={tab}>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-teal-500" />
                </div>
              ) : requests.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <p className="text-slate-500">No {tab} requests</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {requests.map((req) => (
                    <Card key={req.request_id} className="border border-slate-200">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-slate-900">{req.name}</h3>
                              {getStatusBadge(req.status)}
                            </div>
                            <div className="flex items-center gap-4 text-sm text-slate-500 mb-2">
                              <span className="flex items-center gap-1">
                                <Mail className="w-3.5 h-3.5" />
                                {req.email}
                              </span>
                              <span className="flex items-center gap-1">
                                <Building2 className="w-3.5 h-3.5" />
                                {req.company}
                              </span>
                            </div>
                            {req.role && (
                              <p className="text-sm text-slate-600 mb-1">
                                <strong>Role:</strong> {req.role}
                              </p>
                            )}
                            {req.reason && (
                              <p className="text-sm text-slate-600 mb-2">
                                <strong>Reason:</strong> {req.reason}
                              </p>
                            )}
                            <p className="text-xs text-slate-400">
                              Requested: {formatDate(req.created_at)}
                            </p>
                          </div>
                          
                          {req.status === "pending" && (
                            <div className="flex gap-2 flex-shrink-0">
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 hover:bg-red-50"
                                onClick={() => handleReject(req.request_id)}
                                disabled={processing === req.request_id}
                              >
                                {processing === req.request_id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <X className="w-4 h-4" />
                                )}
                              </Button>
                              <Button
                                size="sm"
                                className="bg-teal-500 hover:bg-teal-600"
                                onClick={() => handleApprove(req.request_id)}
                                disabled={processing === req.request_id}
                              >
                                {processing === req.request_id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <Check className="w-4 h-4 mr-1" />
                                    Approve
                                  </>
                                )}
                              </Button>
                            </div>
                          )}
                          
                          {req.status === "revoked" && (
                            <div className="flex gap-2 flex-shrink-0">
                              <Button
                                size="sm"
                                className="bg-teal-500 hover:bg-teal-600"
                                onClick={() => handleRestore(req.request_id)}
                                disabled={processing === req.request_id}
                              >
                                {processing === req.request_id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <RefreshCw className="w-4 h-4 mr-1" />
                                    Restore Access
                                  </>
                                )}
                              </Button>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          ))}

          {/* Whitelist tab */}
          <TabsContent value="whitelist">
            <div className="flex justify-end mb-4">
              <Button onClick={() => setShowAddWhitelist(true)} className="gap-2">
                <Plus className="w-4 h-4" />
                Add Email
              </Button>
            </div>
            
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-teal-500" />
              </div>
            ) : whitelist.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <p className="text-slate-500">No whitelisted emails</p>
                  <p className="text-sm text-slate-400 mt-1">
                    Add emails manually or approve access requests
                  </p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-0">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Email</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Name</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Company</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Added</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {whitelist.map((item) => (
                        <tr key={item.email} className="hover:bg-slate-50">
                          <td className="py-3 px-4 text-sm text-slate-900">{item.email}</td>
                          <td className="py-3 px-4 text-sm text-slate-600">{item.name || "-"}</td>
                          <td className="py-3 px-4 text-sm text-slate-600">{item.company || "-"}</td>
                          <td className="py-3 px-4 text-sm text-slate-500">
                            {item.approved_at ? formatDate(item.approved_at) : "-"}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleRemoveFromWhitelist(item.email)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Add to Whitelist Dialog */}
      <Dialog open={showAddWhitelist} onOpenChange={setShowAddWhitelist}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Email to Whitelist</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddToWhitelist} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="email"
                placeholder="founder@startup.com"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">Name (optional)</Label>
              <Input
                id="name"
                type="text"
                placeholder="John Smith"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="company">Company (optional)</Label>
              <Input
                id="company"
                type="text"
                placeholder="Acme Therapeutics"
                value={newCompany}
                onChange={(e) => setNewCompany(e.target.value)}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddWhitelist(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={addingEmail} className="bg-teal-500 hover:bg-teal-600">
                {addingEmail ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Add to Whitelist
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
